import React, { useEffect, useState } from "react";
import { Modal, Form, Button } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { updateVisitorById } from "../../api/visiteurs";
import { Visitor } from "./visitor";
import { useAcademicYear } from "../../core/common/context/AcademicYearContext";
import {
  Action,
  buildPermission,
  Resource,
} from "../../core/common/autorisations/permissionsMap";
import withPermission from "../../core/common/autorisations/withPermission";

// Schéma Zod pour la validation (identique au create mais id_academicYear optionnel car géré par le contexte)
const visitorSchema = z.object({
  firstname: z.string().min(1, "Le prénom est requis"),
  lastname: z.string().min(1, "Le nom est requis"),
  sexe: z.enum(["Masculin", "Feminin"], {
    required_error: "Le sexe est requis",
  }),
  number: z.string().optional(),
  email: z.string().email("Email invalide").optional().or(z.literal("")),
  source: z.string().optional(),
  establishment_of_origin: z
    .string()
    .min(1, "L'établissement d'origine est requis"),
  level_of_study: z.enum(
    [
      "Baccalauréat",
      "Licence 1",
      "Licence 2",
      "Licence 3",
      "Master 1",
      "Master 2",
    ],
    {
      required_error: "Le niveau d'étude est requis",
    },
  ),
  field_of_study: z.enum(
    [
      "TSECO",
      "TSE",
      "TLL",
      "TSS",
      "TSExp",
      "Management",
      "Informatique",
      "Economie",
      "Droit",
      "Autre",
    ],
    {
      required_error: "Le domaine d'étude est requis",
    },
  ),
  level_desired: z.enum(
    ["Licence 1", "Licence 2", "Licence 3", "Master 1", "Master 2", "Autre"],
    {
      required_error: "Le niveau souhaité est requis",
    },
  ),
  level_of_interest: z.enum(
    [
      "Intéressé",
      "Très Intéressé",
      "Peu Intéressé",
      "Pas Intéressé",
      "A Relancer",
    ],
    {
      required_error: "Le niveau d'intérêt est requis",
    },
  ),
  registration_statut: z.boolean(),
  id_filiere: z.string().min(1, "La filière est requise"),
  otherField_Of_Study: z.string().optional().nullable(),
  otherLevel_Of_Study: z.string().optional().nullable(),
  observation: z.string().min(1, "L'observation est requise"),
});

type VisitorFormData = z.infer<typeof visitorSchema>;

interface Props {
  show: boolean;
  visitor: Visitor | null;
  onClose: () => void;
  onSuccess: (updatedVisitor: Visitor) => void;
  filieres: { id: string; label: string }[];
}

const VisitorEditModal: React.FC<Props> = ({
  show,
  visitor,
  onClose,
  onSuccess,
  filieres,
}) => {
  const { selectedYear } = useAcademicYear();
  const [loading, setLoading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue,
  } = useForm<VisitorFormData>({
    resolver: zodResolver(visitorSchema),
  });

  const fieldOfStudy = watch("field_of_study");
  const levelDesired = watch("level_desired");

  // Charger les données du visiteur dans le formulaire
  useEffect(() => {
    if (visitor) {
      reset({
        firstname: visitor.firstname,
        lastname: visitor.lastname,
        sexe: visitor.sexe as any,
        number: visitor.number,
        email: visitor.email,
        source: visitor.source,
        establishment_of_origin: visitor.establishment_of_origin,
        level_of_study: visitor.level_of_study as any,
        field_of_study: visitor.field_of_study as any,
        level_desired: visitor.level_desired as any,
        level_of_interest: visitor.level_of_interest as any,
        registration_statut: visitor.registration_statut,
        id_filiere: visitor.id_filiere,
        otherField_Of_Study: visitor.otherField_Of_Study,
        otherLevel_Of_Study: visitor.otherLevel_Of_Study,
        observation: visitor.observation,
      });
    }
  }, [visitor, reset]);

  const onSubmit = async (data: VisitorFormData) => {
    if (!visitor) return;
    try {
      setLoading(true);
      const response = await updateVisitorById(visitor.id, {
        ...data,
        id_academicYear: selectedYear?.id || visitor.id_academicYear,
      });
      if (response.success) {
        toast.success("Visiteur modifié avec succès");
        onSuccess(response.data);
        onClose();
      } else {
        toast.error(response.message || "Erreur lors de la modification");
      }
    } catch (error: any) {
      // console.error(error);
      toast.error(
        error.message || "Erreur lors de la modification du visiteur",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={onClose}
      centered
      size="lg"
      className="visitor-create-modal"
    >
      <Modal.Header closeButton className="border-0 pb-0 pt-4 px-4">
        <Modal.Title className="fw-bold">Modifier le Visiteur</Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSubmit(onSubmit)}>
        <Modal.Body className="px-4 py-3">
          <div className="row g-4">
            {/* Informations de base */}
            <div className="col-md-6">
              <div className="d-flex align-items-center mb-3">
                <div className="bg-primary-transparent text-primary p-2 rounded-3 me-2">
                  <i className="ti ti-user fs-18"></i>
                </div>
                <h6 className="fw-bold mb-0">Informations de Base</h6>
              </div>

              <div className="row g-2">
                <Form.Group className="col-6 mb-2">
                  <Form.Label className="small fw-semibold text-dark">
                    Prénom <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    className="form-control-sm border-2"
                    {...register("firstname")}
                    isInvalid={!!errors.firstname}
                  />
                </Form.Group>
                {errors.firstname && (
                  <div className="invalid-feedback">
                    {errors.firstname.message}
                  </div>
                )}
                <Form.Group className="col-6 mb-2">
                  <Form.Label className="small fw-semibold text-dark">
                    Nom <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    className="form-control-sm border-2"
                    {...register("lastname")}
                    isInvalid={!!errors.lastname}
                  />
                </Form.Group>
                {errors.lastname && (
                  <div className="invalid-feedback">
                    {errors.lastname.message}
                  </div>
                )}
              </div>

              <div className="row g-2">
                <Form.Group className="col-6 mb-2">
                  <Form.Label className="small fw-semibold text-dark">
                    Sexe <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Select
                    className="form-select-sm border-2"
                    {...register("sexe")}
                    isInvalid={!!errors.sexe}
                  >
                    <option value="">Sélectionner</option>
                    <option value="Masculin">Masculin</option>
                    <option value="Feminin">Feminin</option>
                  </Form.Select>
                </Form.Group>
                {errors.sexe && (
                  <div className="invalid-feedback">{errors.sexe.message}</div>
                )}
                <Form.Group className="col-6 mb-2">
                  <Form.Label className="small fw-semibold text-dark">
                    Téléphone
                  </Form.Label>
                  <Form.Control
                    type="text"
                    className="form-control-sm border-2"
                    {...register("number")}
                    isInvalid={!!errors.number}
                  />
                </Form.Group>
                {errors.number && (
                  <div className="invalid-feedback">
                    {errors.number.message}
                  </div>
                )}
              </div>

              <Form.Group className="mb-2">
                <Form.Label className="small fw-semibold text-dark">
                  Email
                </Form.Label>
                <Form.Control
                  type="email"
                  className="form-control-sm border-2"
                  {...register("email")}
                  isInvalid={!!errors.email}
                />
              </Form.Group>
              {errors.email && (
                <div className="invalid-feedback">{errors.email.message}</div>
              )}
              <Form.Group className="mb-0">
                <Form.Label className="small fw-semibold text-dark">
                  Source
                </Form.Label>
                <Form.Control
                  type="text"
                  className="form-control-sm border-2"
                  {...register("source")}
                  isInvalid={!!errors.source}
                />
              </Form.Group>
              {errors.source && (
                <div className="invalid-feedback">{errors.source.message}</div>
              )}
            </div>

            {/* Parcours académique */}
            <div className="col-md-6">
              <div className="d-flex align-items-center mb-3">
                <div className="bg-success-transparent text-success p-2 rounded-3 me-2">
                  <i className="ti ti-school fs-18"></i>
                </div>
                <h6 className="fw-bold mb-0">Parcours Académique</h6>
              </div>

              <Form.Group className="mb-2">
                <Form.Label className="small fw-semibold text-dark">
                  Établissement d'origine *
                </Form.Label>
                <Form.Control
                  type="text"
                  className="form-control-sm border-2"
                  {...register("establishment_of_origin")}
                  isInvalid={!!errors.establishment_of_origin}
                />
              </Form.Group>

              <div className="row g-2">
                <Form.Group className="col-6 mb-2">
                  <Form.Label className="small fw-semibold text-dark">
                    Niveau actuel <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Select
                    className="form-select-sm border-2"
                    {...register("level_of_study")}
                    isInvalid={!!errors.level_of_study}
                  >
                    <option value="">Sélectionner</option>
                    <option value="Baccalauréat">Baccalauréat</option>
                    <option value="Licence 1">Licence 1</option>
                    <option value="Licence 2">Licence 2</option>
                    <option value="Licence 3">Licence 3</option>
                    <option value="Master 1">Master 1</option>
                    <option value="Master 2">Master 2</option>
                  </Form.Select>
                  {errors.level_of_study && (
                    <div className="invalid-feedback">
                      {errors.level_of_study.message}
                    </div>
                  )}
                </Form.Group>

                <Form.Group className="col-6 mb-2">
                  <Form.Label className="small fw-semibold text-dark">
                    Domaine actuel <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Select
                    className="form-select-sm border-2"
                    {...register("field_of_study")}
                    isInvalid={!!errors.field_of_study}
                  >
                    <option value="">Sélectionner</option>
                    <option value="TSECO">TSECO</option>
                    <option value="TSE">TSE</option>
                    <option value="TLL">TLL</option>
                    <option value="TSS">TSS</option>
                    <option value="TSExp">TSExp</option>
                    <option value="Management">Management</option>
                    <option value="Informatique">Informatique</option>
                    <option value="Economie">Economie</option>
                    <option value="Droit">Droit</option>
                    <option value="Autre">Autre</option>
                  </Form.Select>
                </Form.Group>
                {errors.field_of_study && (
                  <div className="invalid-feedback">
                    {errors.field_of_study.message}
                  </div>
                )}
              </div>

              {fieldOfStudy === "Autre" && (
                <>
                  <Form.Group className="mb-2">
                    <Form.Label className="small fw-semibold text-dark">
                      Autre domaine d'étude{" "}
                      <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      className="form-control-sm border-2"
                      {...register("otherField_Of_Study")}
                      isInvalid={!!errors.otherField_Of_Study}
                    />
                  </Form.Group>
                  {errors.otherField_Of_Study && (
                    <div className="invalid-feedback">
                      {errors.otherField_Of_Study.message}
                    </div>
                  )}
                </>
              )}

              <div className="row g-2">
                <Form.Group className="col-6 mb-2">
                  <Form.Label className="small fw-semibold text-dark">
                    Niveau souhaité <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Select
                    className="form-select-sm border-2"
                    {...register("level_desired")}
                    isInvalid={!!errors.level_desired}
                  >
                    <option value="">Sélectionner</option>
                    <option value="Licence 1">Licence 1</option>
                    <option value="Licence 2">Licence 2</option>
                    <option value="Licence 3">Licence 3</option>
                    <option value="Master 1">Master 1</option>
                    <option value="Master 2">Master 2</option>
                    <option value="Autre">Autre</option>
                  </Form.Select>
                </Form.Group>
                {errors.level_desired && (
                  <div className="invalid-feedback">
                    {errors.level_desired.message}
                  </div>
                )}
                <Form.Group className="col-6 mb-2">
                  <Form.Label className="small fw-semibold text-dark">
                    Filière souhaitée <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Select
                    className="form-select-sm border-2"
                    {...register("id_filiere")}
                    isInvalid={!!errors.id_filiere}
                  >
                    <option value="">Sélectionner</option>
                    {filieres.map((filiere) => (
                      <option key={filiere.id} value={filiere.id}>
                        {filiere.label}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </div>

              {levelDesired === "Autre" && (
                <>
                  <Form.Group className="mb-2">
                    <Form.Label className="small fw-semibold text-dark">
                      Autre niveau souhaité{" "}
                      <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      className="form-control-sm border-2"
                      {...register("otherLevel_Of_Study")}
                      isInvalid={!!errors.otherLevel_Of_Study}
                    />
                  </Form.Group>
                  {errors.otherLevel_Of_Study && (
                    <div className="invalid-feedback">
                      {errors.otherLevel_Of_Study.message}
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Intérêts et observations */}
            <div className="col-12 mt-2">
              <div
                className="p-3 rounded-3 "
                style={{ backgroundColor: "#f8f9fa" }}
              >
                <div className="row g-3">
                  <div className="col-md-6">
                    <Form.Group>
                      <Form.Label className="small fw-semibold text-dark">
                        Niveau d'intérêt <span className="text-danger">*</span>
                      </Form.Label>
                      <div className="dropdown border-2 border-solid">
                        <button
                          className="btn btn-sm btn-white dropdown-toggle w-100 text-start"
                          style={{
                            backgroundColor: "#f8f9fa",
                            border: "1px solid #ced4da",
                          }}
                          type="button"
                          data-bs-toggle="dropdown"
                          aria-expanded="false"
                        >
                          {watch("level_of_interest") || "Sélectionner"}
                        </button>
                        <ul className="dropdown-menu w-100">
                          {[
                            "Pas Intéressé",
                            "Peu Intéressé",
                            "Intéressé",
                            "Très Intéressé",
                            "A Relancer",
                          ].map((level) => (
                            <li key={level}>
                              <label className="dropdown-item d-flex align-items-center mb-0">
                                <input
                                  type="checkbox"
                                  className="form-check-input me-2 "
                                  checked={watch("level_of_interest") === level}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setValue(
                                        "level_of_interest",
                                        level as any,
                                      );
                                    }
                                  }}
                                />
                                {level}
                              </label>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </Form.Group>
                    {errors.level_of_interest && (
                      <div className="invalid-feedback">
                        {errors.level_of_interest.message}
                      </div>
                    )}

                    <Form.Group className="mt-2">
                      <Form.Check
                        type="switch"
                        id="reg-status-edit"
                        label={
                          <span className="small fw-semibold text-dark">
                            Statut d'inscription (Inscrit)
                          </span>
                        }
                        {...register("registration_statut")}
                      />
                    </Form.Group>
                    {errors.registration_statut && (
                      <div className="invalid-feedback">
                        {errors.registration_statut.message}
                      </div>
                    )}
                  </div>

                  <div className="col-md-6">
                    <Form.Group>
                      <Form.Label className="small fw-semibold text-dark">
                        Observations <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        style={{
                          backgroundColor: "#f8f9fa",
                          border: "1px solid #ced4da",
                        }}
                        as="textarea"
                        rows={3}
                        className="form-control-sm border-2"
                        {...register("observation")}
                        isInvalid={!!errors.observation}
                        placeholder="Notes complémentaires..."
                      />
                    </Form.Group>
                    {errors.observation && (
                      <div className="invalid-feedback">
                        {errors.observation.message}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Modal.Body>

        <Modal.Footer className="border-0 px-4 pb-4 pt-0">
          <Button
            variant="outline-secondary"
            className="rounded-2 px-4 mx-2"
            onClick={onClose}
            disabled={loading}
          >
            Annuler
          </Button>
          <Button
            type="submit"
            variant="dark"
            className="rounded-2 px-4"
            disabled={loading}
          >
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2"></span>
                Modification...
              </>
            ) : (
              "Enregistrer les modifications"
            )}
          </Button>
        </Modal.Footer>
      </Form>

      <style>{`
        .visitor-create-modal .modal-content {
          border-radius: 20px;
          border: none;
          box-shadow: 0 20px 40px rgba(0,0,0,0.1);
        }

        .form-control-sm, .form-select-sm {
          padding-top: 0.4rem;
          padding-bottom: 0.4rem;
          border-color: #f1f1f1;
        }

        .form-control:focus, .form-select:focus {
          border-color: #111;
          box-shadow: none;
        }

        .bg-primary-transparent { background: rgba(13, 110, 253, 0.1); }
        .bg-success-transparent { background: rgba(25, 135, 84, 0.1); }

        .border-dashed { border-style: dashed !important; }

        .form-label { margin-bottom: 0.25rem; }
      `}</style>
    </Modal>
  );
};

export default withPermission(
  VisitorEditModal,
  [buildPermission(Resource.VISITOR, Action.UPDATE)],
  "masquer",
);
